import { GenericTableColumn } from "gen/model/genericTableColumn";
import { GenericTableRow } from "gen/model/genericTableRow";
import { useMemo } from "react";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import { GenericTableAgGridReactProps } from "shared/components/table/data/DataTableTypes";
import { ROW_ID_TOTAL } from "shared/components/table/data/internal/DataTableConstants";

export const useDataTableRowsAgProps = (
  tableProps: DataTableProps,
  tableContext: DataTableContext,
) => {
  const {
    data: { columns, rows, client_row_aggregation },
    serverSideDatasource,
  } = tableProps;
  const { showTotalRow = hasAggregatableColumns(columns) } = tableContext;

  // This is a separate memo so row uuids do not change when the layout changes
  const flatRows = useMemo(
    () => (client_row_aggregation ? rows : flattenRows(rows)),
    [client_row_aggregation, rows],
  );

  return useMemo((): GenericTableAgGridReactProps => {
    if (serverSideDatasource) {
      return {
        rowModelType: "serverSide",
        serverSideDatasource,
        serverSidePivotResultFieldSeparator: "|",
      };
    }
    if (!flatRows.length) {
      return {
        rowData: [],
        pinnedBottomRowData: [],
      };
    }

    const rowData = flatRows.filter((r) => r.granularity_level !== "total");
    const pinnedBottomRowData: GenericTableRow[] = flatRows.filter(
      (r) => r.granularity_level === "total",
    );
    if (client_row_aggregation && showTotalRow) {
      pinnedBottomRowData.push({
        id: ROW_ID_TOTAL,
        cells: {},
        granularity_level: "total",
      });
    }

    return {
      rowData,
      pinnedBottomRowData,
    };
  }, [serverSideDatasource, flatRows, client_row_aggregation, showTotalRow]);
};

const hasAggregatableColumns = (columns: GenericTableColumn[]): boolean =>
  columns.some(
    (col) =>
      col.aggregatable || (col.columns && hasAggregatableColumns(col.columns)),
  );

const flattenRows = (rows: GenericTableRow[]): GenericTableRow[] =>
  rows.flatMap((row) => [row, ...flattenRows(row.child_rows ?? [])]);
